const ApplicationContext = require("../shared/config/ApplicationContext.js");
const RegionUtil = require("../shared/util/RegionUtil.js");
const LoginUtil = require("../shared/util/LoginUtil.js");

/**
 * Reactive regional session recovery.
 *
 * The session and oauth_token cookies are HttpOnly, so the client cannot inspect
 * their expiry; it reacts to the server's responses instead, following
 * docs/REGIONALIZATION.md ("Re-establishing a regional session"):
 *
 *   region call
 *     non-401 -> use the existing region session (return the response as-is)
 *     401     -> recoverSession:
 *       POST {region}/actions/login/oauthtoken       (mint region session from JWT cookie)
 *         200      -> RECOVERED (retry original call)
 *         otherwise (oauth_token expired):
 *           POST US /actions/refreshToken            (refresh JWT from US session)
 *             not 204 -> LOGIN (US session gone -> redirect to login)
 *             204     -> POST oauthtoken again
 *                          200  -> RECOVERED (retry original call)
 *                          else -> LOGIN
 *
 * Concurrent calls share a single in-flight oauthtoken call per region and a single
 * in-flight refreshToken call, so a page firing several parallel requests re-auths once.
 */

const RecoveryStatus = {
    RECOVERED: "recovered",
    LOGIN: "login",
    FAILED: "failed",
};

const inFlightRegion = new Map(); // regionId -> Promise<Response>
let inFlightRefresh = null;       // Promise<Response>
let redirecting = false;          // guard against multiple login redirects

async function establishRegionCSSession(regionId) {
    if (inFlightRegion.has(regionId)) return inFlightRegion.get(regionId);

    const tenant = ApplicationContext.contentServer.tenant;
    const server = RegionUtil.getRegionServer(regionId);
    try {
        const request = fetch(`${server}/${tenant}/actions/login/oauthtoken`, {
            method: "POST",
            credentials: "include"
        });
        inFlightRegion.set(regionId, request);
        return await request;
    } finally {
        inFlightRegion.delete(regionId);
    }
}

// Refreshes the US OAuth token cookie (_circuits_oauth_token in prod, _circuits_staging_oauth_token in staging)
async function refreshUsOauthToken() {
    if (inFlightRefresh) return inFlightRefresh;

    try {
        inFlightRefresh = fetch(`${ApplicationContext.usContentUrl()}/actions/refreshToken`, {
            method: "POST",
            credentials: "include"
        });
        return await inFlightRefresh;
    } finally {
        inFlightRefresh = null;
    }
}

async function recoverSession(regionId) {
    try {
        const regionSessionRes = await establishRegionCSSession(regionId);
        if (regionSessionRes?.status === 200) return RecoveryStatus.RECOVERED;

        const refreshUsOauthTokenRes = await refreshUsOauthToken();
        if (refreshUsOauthTokenRes?.status !== 204) return RecoveryStatus.LOGIN;

        const retryRegionSessionRes = await establishRegionCSSession(regionId);
        if (retryRegionSessionRes?.status === 200) return RecoveryStatus.RECOVERED;

        return RecoveryStatus.LOGIN;
    } catch (e) {
        console.warn("SessionRecovery: recovery failed");
        return RecoveryStatus.FAILED;
    }
}

function redirectToLogin() {
    if (redirecting) return;

    try {
        redirecting = true;
        require("../shared/util/LoginUtil.js").goLogin();
    } catch (e) {
        redirecting = false;
        console.warn("SessionRecovery: Login redirect failed");
    }
}

async function withSessionRecovery(regionId, doFetch) {
    const res = await doFetch();
    if (res?.status !== 401 || !LoginUtil.isLogin()) return res;

    const outcome = await recoverSession(regionId);
    if (outcome === RecoveryStatus.RECOVERED) return doFetch();
    if (outcome === RecoveryStatus.LOGIN) redirectToLogin();

    return res;
}

module.exports = {
    RecoveryStatus,
    establishRegionCSSession,
    withSessionRecovery
};
